import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Stack, Typography, } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import RoomServiceIcon from '@mui/icons-material/RoomService'
import { useGetLojaAtualQuery, useLazyGetContaTabletQuery, useLazyValidarIdentificadorSalaoQuery } from '../api'
import { canalDoIdentificador, ehDispositivoFixo } from '../lib/canal'
import { useImagensPorProduto } from '../lib/imagens-catalogo'
import { contextoSalao } from '../lib/salao'
import { destinoChamadoConfigurado } from '../lib/destino-chamado'
import { abrirChamarGarcom, onAbrirConta, ESTILO_BOTAO_CONTA, ESTILO_BOTAO_GARCOM } from '../lib/tablet-eventos'
import { consolidarConta, itensParaListagem, rotuloOrigem } from '../lib/conta'
import { formatarBRL, formatarData } from '../lib/formato'
import { ListaItensPedido } from './lista-itens-pedido'
import { CampoComanda } from './campo-comanda'
import { TemaKiosk } from './catalogo-kiosk/tema-kiosk'
import { DialogTitle } from './dialog-componentes'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

export function ContaSalao(): JSX.Element | null {
  const { data: loja } = useGetLojaAtualQuery()
  const [aberto, setAberto] = useState(false)
  const [numero, setNumero] = useState('')
  const [consultou, setConsultou] = useState(false)
  const [consultar, { data: conta, isFetching, error }] = useLazyGetContaTabletQuery()
  const [validarIdentificador] = useLazyValidarIdentificadorSalaoQuery()
  const [erroIdentificador, setErroIdentificador] = useState<string | null>(null)
  const refNumero = useRef<HTMLInputElement>(null)
  const ctx = contextoSalao(loja)
  const kiosk = !!loja && ehDispositivoFixo(loja.identificador)
  const imagens = useImagensPorProduto(loja ? canalDoIdentificador(loja.identificador) : 'CARDAPIO_COMANDA_MESA')
  const pedirPrincipal = ctx.ativo && !ctx.principalConhecido

  const buscar = useCallback(
    (identificador: string) => {
      void consultar({ tipo: ctx.tipoPrincipal, identificador, itens: true })
      setConsultou(true)
    },
    [consultar, ctx.tipoPrincipal],
  )

  async function consultarInformado(numeroLido?: string): Promise<void> {
    const num = (numeroLido ?? numero).trim()
    if (!num) return

    setErroIdentificador(null)

    const r = await validarIdentificador({ identificador: num, tipo: ctx.tipoPrincipal }).unwrap().catch(() => null)
    if (!r?.valida) {
      setErroIdentificador(`${ctx.rotuloPrincipal === 'mesa' ? 'Mesa' : 'Comanda'} não encontrada ou inativa. Confira o número com o garçom.`,)
      return
    }

    const numeroCadastro = r.numero ?? num

    if (numeroCadastro !== num) setNumero(numeroCadastro)

    buscar(numeroCadastro)
  }

  const pedirNumero = pedirPrincipal && !consultou
  const identificadorAtual = ctx.principalConhecido ? ctx.principalNumero : numero.trim()
  const resumo = consolidarConta(conta)
  const vazia = !!conta && conta.pedidos.length === 0
  const rotuloLocal = `${ctx.rotuloPrincipal} ${conta?.identificador ?? identificadorAtual}`
  const modeloDaLoja = conta?.modeloIdentificador
  const mostrarComandaDoPedido = !!modeloDaLoja && modeloDaLoja !== ctx.tipoPrincipal

  useEffect(
    () =>
      onAbrirConta(() => {
        setNumero('')
        setConsultou(false)
        setErroIdentificador(null)
        setAberto(true)

        const c = contextoSalao(loja)

        if (c.principalConhecido && c.principalNumero) {
          void consultar({ tipo: c.tipoPrincipal, identificador: c.principalNumero, itens: true })
          setConsultou(true)
        }
      }),
    [loja, consultar],
  )

  useEffect(() => {
    if (!aberto || !pedirPrincipal || consultou) return

    const t = setTimeout(() => refNumero.current?.focus(), 150)

    return () => clearTimeout(t)
  }, [aberto, pedirPrincipal, consultou])

  // Depois dos hooks: o contexto muda com a loja, e return antes deles derruba a tela.
  if (!ctx.ativo) return null

  const corpo = (
    <>
      <DialogTitle onClose={() => setAberto(false)} component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant={kiosk ? 'h4' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Sua conta
            </Typography>
            {!pedirNumero && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {rotuloLocal}
              </Typography>
            )}
          </Box>

          {!pedirNumero && identificadorAtual && (
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => buscar(identificadorAtual)}
              disabled={isFetching}
              sx={{ flexShrink: 0, fontWeight: 600 }}
            >
              Atualizar
            </Button>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {pedirNumero ? (
          <>
            <Box sx={{ mt: 1 }}>
              <CampoComanda
                rotulo={`Número da ${ctx.rotuloPrincipal}`}
                valor={numero}
                aoMudar={(v) => {
                  setNumero(v)
                  if (erroIdentificador) setErroIdentificador(null)
                }}
                autoFocus
                inputRef={refNumero}
                erro={erroIdentificador}
                tipoEsperado={ctx.tipoPrincipal}
                aoConfirmar={(informado) => void consultarInformado(informado)}
              />
            </Box>
          </>
        ) : isFetching && !conta ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">Não foi possível buscar a conta. Tente novamente.</Alert>
        ) : vazia ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            Nenhum pedido em aberto na {rotuloLocal}.
          </Typography>
        ) : conta ? (
          <Stack spacing={2.5}>
            {conta.pedidos.map((p, i) => {
              const origem = rotuloOrigem(p.tipoImportacao)
              const hora = p.dataPedido ? formatarData(p.dataPedido, 'hora') : null
              const itens = itensParaListagem(p.itens, String(p.idPedidoIntegracao ?? i), imagens)

              return (
                <Box key={p.codigoIntegracao ?? p.idPedidoIntegracao ?? i}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: itens.length > 0 ? 1 : 0 }}
                  >
                    {mostrarComandaDoPedido && p.identificadorPrincipal && (
                      <Chip
                        size="small"
                        color="primary"
                        label={`${modeloDaLoja === 'COMANDA' ? 'Comanda' : 'Mesa'} ${p.identificadorPrincipal}`}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {origem && <Chip size="small" label={origem} variant="outlined" />}
                    {hora && (
                      <Typography variant="body2" color="text.secondary">
                        {hora}
                      </Typography>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {formatarBRL(p.total)}
                    </Typography>
                  </Stack>

                  {itens.length > 0 && (
                    <ListaItensPedido itens={itens} tamanho={kiosk ? 'grande' : 'normal'} />
                  )}
                </Box>
              )
            })}

            <Divider />

            <Stack spacing={0.5}>
              {resumo.taxaServico > 0 && (
                <>
                  <Linha rotulo="Subtotal" valor={formatarBRL(resumo.subtotal)} />
                  <Linha rotulo="Taxa de serviço" valor={formatarBRL(resumo.taxaServico)} />
                </>
              )}
              {resumo.desconto > 0 && (
                <Linha rotulo="Desconto" valor={`- ${formatarBRL(resumo.desconto)}`} />
              )}

              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ pt: 0.5 }}>
                <Typography variant={kiosk ? 'h5' : 'h6'}>Total</Typography>
                <Typography variant={kiosk ? 'h3' : 'h5'} sx={{ fontWeight: 800 }} color="primary.main">
                  {formatarBRL(resumo.total)}
                </Typography>
              </Stack>
            </Stack>

            <Alert severity="info" sx={{ alignItems: 'center' }}>
              Valores dos pedidos em aberto. O pagamento é feito com o atendente.
            </Alert>
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: kiosk ? 2 : 1, gap: 1 }}>
        <Box sx={{ flexGrow: 1 }} />

        {pedirNumero ? (
          <Button
            {...ESTILO_BOTAO_CONTA}
            size={kiosk ? 'large' : 'medium'}
            onClick={() => void consultarInformado()}
            disabled={!numero.trim() || isFetching}
            sx={{ fontWeight: 700, px: kiosk ? 4 : 2 }}
            startIcon={<ReceiptLongIcon />}
          >
            Ver a conta
          </Button>
        ) : (
          ctx.mesaFisica != null && destinoChamadoConfigurado(loja) && (
            <Button
              {...ESTILO_BOTAO_GARCOM}
              size={kiosk ? 'large' : 'medium'}
              startIcon={<RoomServiceIcon />}
              onClick={() => {
                setAberto(false)
                abrirChamarGarcom()
              }}
              sx={{ fontWeight: 700, px: kiosk ? 4 : 2 }}
            >
              Chamar garçom
            </Button>
          )
        )}
      </DialogActions>
    </>
  )

  const dialog = (
    <Dialog
      open={aberto}
      onClose={() => setAberto(false)}
      fullWidth
      maxWidth={kiosk ? 'sm' : 'xs'}
      slotProps={{
        paper: {
          sx: kiosk ? {
            borderRadius: 4,
            maxHeight: '90vh',
            '@supports (max-height: 90dvh)': { maxHeight: '90dvh' },
          } : undefined,
        }
      }}
    >
      {corpo}
    </Dialog>
  )

  return kiosk ? <TemaKiosk>{dialog}</TemaKiosk> : dialog
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {rotulo}
      </Typography>

      <Typography variant="body2">
        {valor}
      </Typography>
    </Stack>
  )
}
